import { ALL_LOCALES, DEFAULT_LOCALE, type Locale } from "./locales";

export type PurchaserLocaleSource =
  | "explicit"
  | "customer_preference"
  | "order"
  | "request"
  | "team_default"
  | "fallback";

export type GiftRecipientLocaleSource = "explicit" | "fallback";

export interface ResolvedShopperLocale<Source extends string> {
  locale: Locale;
  source: Source;
}

function isLocale(value: string): value is Locale {
  return ALL_LOCALES.find((locale) => locale === value) !== undefined;
}

/** Exact catalog locale, or language prefix (`es-MX` → `es`). */
export function parseLocale(value: string | null | undefined): Locale | undefined {
  if (value == null) {
    return undefined;
  }
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) {
    return undefined;
  }
  if (isLocale(trimmed)) {
    return trimmed;
  }
  const prefix = trimmed.split("-")[0];
  if (prefix && isLocale(prefix)) {
    return prefix;
  }
  return undefined;
}

function firstLocale(
  values: readonly (string | null | undefined)[] | null | undefined,
): Locale | undefined {
  if (!values) {
    return undefined;
  }
  for (const value of values) {
    const locale = parseLocale(value);
    if (locale) {
      return locale;
    }
  }
  return undefined;
}

/** explicit → Customer.language → order → request → team default → en */
export function resolvePurchaserLocale(input: {
  explicitLocale?: string | null;
  customerLanguage?: string | null;
  orderLocale?: string | null;
  requestLanguages?: readonly string[] | null;
  teamDefaultLocale?: string | null;
}): ResolvedShopperLocale<PurchaserLocaleSource> {
  const explicit = parseLocale(input.explicitLocale);
  if (explicit) {
    return { locale: explicit, source: "explicit" };
  }
  const customer = parseLocale(input.customerLanguage);
  if (customer) {
    return { locale: customer, source: "customer_preference" };
  }
  const order = parseLocale(input.orderLocale);
  if (order) {
    return { locale: order, source: "order" };
  }
  const request = firstLocale(input.requestLanguages);
  if (request) {
    return { locale: request, source: "request" };
  }
  const teamDefault = parseLocale(input.teamDefaultLocale);
  if (teamDefault) {
    return { locale: teamDefault, source: "team_default" };
  }
  return { locale: DEFAULT_LOCALE, source: "fallback" };
}

/** Portal locale only; never inherit purchaser language. Invalid → en. */
export function resolveGiftRecipientLocale(input: {
  portalLocale?: string | null;
}): ResolvedShopperLocale<GiftRecipientLocaleSource> {
  const explicit = parseLocale(input.portalLocale);
  if (explicit) {
    return { locale: explicit, source: "explicit" };
  }
  return { locale: DEFAULT_LOCALE, source: "fallback" };
}

export interface ShopperLocaleSnapshot {
  customerLocale: Locale;
  giftRecipient?: { locale: Locale };
}

/** Persist on create. Merchant UI language is never a shopper locale. */
export function snapshotShopperLocales(input: {
  portalLocale?: string | null;
  customerLanguage?: string | null;
  orderLocale?: string | null;
  teamDefaultLocale?: string | null;
  merchantCreated?: boolean;
  captureGiftRecipientLocale?: boolean;
}): ShopperLocaleSnapshot {
  const purchaser = resolvePurchaserLocale({
    customerLanguage: input.customerLanguage,
    orderLocale: input.orderLocale,
    requestLanguages:
      input.merchantCreated || input.portalLocale == null
        ? undefined
        : [input.portalLocale],
    teamDefaultLocale: input.teamDefaultLocale,
  });
  const snapshot: ShopperLocaleSnapshot = {
    customerLocale: purchaser.locale,
  };
  if (input.captureGiftRecipientLocale) {
    snapshot.giftRecipient = {
      locale: resolveGiftRecipientLocale({
        portalLocale: input.portalLocale,
      }).locale,
    };
  }
  return snapshot;
}
